import { List, ListItemButton, Stack, Typography } from '@mui/material';

import CasLink from 'src/compass/shared/components/CasLink/CasLink';
import Iconify from 'src/components/iconify';

// Direct paths, not the application barrel: this file is inside it, and importing it from itself
// is a cycle (see CasPendingEmailsPill in CasContextProviders).
import BatchRowListItem from './BatchRowListItem';
import { IBatchRow, IBatchTerms, RowChannelType } from './batchRow';

// The pinned first entry.  A sentinel rather than a row key so it can never collide with one.
export const DEFAULT_KEY = ' default';

interface Props {
  rows: IBatchRow[];
  terms: IBatchTerms;
  included: Set<string>;
  getRowCustom: (key: string) => { recipients: boolean; message: boolean; channel: boolean };
  // Resolved by the dialog, which owns the per-row overrides it depends on.
  resolveRow: (row: IBatchRow) => RowChannelType;
  needsAttentionCount: number;
  selectedKey: string;
  onSelect: (key: string) => void;
  onToggleInclude: (key: string) => void;
  onIncludeAll: (include: boolean) => void;
}

export default function BatchRowList({
  rows,
  terms,
  included,
  getRowCustom,
  resolveRow,
  needsAttentionCount,
  selectedKey,
  onSelect,
  onToggleInclude,
  onIncludeAll,
}: Props) {
  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 1, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Typography variant="subtitle2">
          Messages {included.size}/{rows.length}
        </Typography>
        <Stack direction="row" spacing={1}>
          <CasLink onClick={() => onIncludeAll(true)}>All</CasLink>
          <CasLink onClick={() => onIncludeAll(false)}>None</CasLink>
        </Stack>
      </Stack>

      <List disablePadding sx={{ overflowY: 'auto', flexGrow: 1 }}>
        <ListItemButton
          selected={selectedKey === DEFAULT_KEY}
          onClick={() => onSelect(DEFAULT_KEY)}
          sx={{ py: 1, px: 1, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Iconify icon="eva:star-fill" width="16px" height="16px" />
            <Typography variant="subtitle2">Default message</Typography>
          </Stack>
        </ListItemButton>

        {rows.length === 0 && (
          <Typography variant="body2" sx={{ p: 2, color: 'text.secondary' }}>
            {terms.emptyList}
          </Typography>
        )}

        {/* Derived here into primitives, and the handlers passed straight through, so each row can
            memoize on them - see BatchRowListItem. */}
        {rows.map((row) => {
          const custom = getRowCustom(row.key);
          const { channel, reason } = resolveRow(row);

          return (
            <BatchRowListItem
              key={row.key}
              row={row}
              selected={selectedKey === row.key}
              included={included.has(row.key)}
              /*
                Recipients and channel are one label, not two. Pinning a channel re-addresses the
                row's contacts, so a row with edited recipients has had its channel settled as
                well - saying both would be saying the same thing twice. The narrower fact wins:
                "To/CC" when the sender chose the recipients, "Send as" when all they did was pick
                the channel.
              */
              customLabel={[
                custom.recipients ? 'To/CC' : (custom.channel && 'Send as') || null,
                custom.message && 'Msg',
              ]
                .filter(Boolean)
                .join(', ')}
              channel={channel}
              reason={reason}
              onSelect={onSelect}
              onToggleInclude={onToggleInclude}
            />
          );
        })}
      </List>

      {needsAttentionCount > 0 && (
        <Typography
          variant="caption"
          sx={{ px: 1, py: 0.5, color: 'text.secondary', borderTop: '1px solid', borderColor: 'divider' }}
        >
          {needsAttentionCount} need{needsAttentionCount === 1 ? 's' : ''} attention
        </Typography>
      )}
    </>
  );
}
